import React, { useState, useRef, useEffect } from "react";
import ImgTag from "../Helpers/ImgTag";

const Product360View = ({ images, maxFps = 30, dragSensitivity = 8 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingSpeed, setDraggingSpeed] = useState(0);
  const [scaleLevel, setScaleLevel] = useState(1);
  const [isScalingUp, setIsScalingUp] = useState(true); // Track the scaling direction
  const [position, setPosition] = useState({ x: 0, y: 0 }); // Track the image position for panning
  const dragStartX = useRef(0);
  const dragStartY = useRef(0); // For vertical dragging
  const dragStartTime = useRef(0);
  const dragDistanceX = useRef(0);
  const dragDistanceY = useRef(0); // For vertical dragging
  const intervalRef = useRef(null);

  // console.log("images", images);

  useEffect(() => {
    preloadImages(images);
  }, []);

  const nextImage = (direction = 1) => {
    setCurrentIndex(
      (prevIndex) => (prevIndex + direction + images.length) % images.length
    );
  };

  const startDrag = (event) => {
    event.preventDefault(); // Prevent default drag behavior on the image
    setIsDragging(true);
    dragStartX.current = event?.clientX || event?.touches[0]?.clientX;
    dragStartY.current = event?.clientY || event?.touches[0]?.clientY;
    dragStartTime.current = Date.now();
    dragDistanceX.current = 0;
    dragDistanceY.current = 0;

    clearInterval(intervalRef.current);
  };

  const onDrag = (event) => {
    if (!isDragging) return;

    event.preventDefault(); // Prevent default drag behavior on the image
    const currentX = event.clientX || event.touches[0].clientX;
    const currentY = event.clientY || event.touches[0].clientY;
    dragDistanceX.current = currentX - dragStartX.current;
    dragDistanceY.current = currentY - dragStartY.current;

    if (scaleLevel === 1) {
      // Rotate images when not zoomed in
      const adjustedDistanceX = dragDistanceX.current / dragSensitivity;
      if (Math.abs(adjustedDistanceX) > 1) {
        nextImage(adjustedDistanceX > 0 ? -1 : 1);
        dragStartX.current = currentX;
        dragStartTime.current = Date.now();
      }
    } else {
      // Pan the image when zoomed in
      setPosition((prevPosition) => ({
        x: prevPosition.x + dragDistanceX.current,
        y: prevPosition.y + dragDistanceY.current,
      }));
      dragStartX.current = currentX;
      dragStartY.current = currentY;
    }
  };

  const stopDrag = () => {
    if (!isDragging) return;

    setIsDragging(false);
    if (scaleLevel === 1) {
      // Momentum scroll for image rotation
      const dragTime = Date.now() - dragStartTime.current;
      const speed = Math.abs(dragDistanceX.current / dragTime);
      setDraggingSpeed(speed);

      if (speed > 0) {
        const frameTime = Math.min(1000 / speed, 1000 / maxFps);

        intervalRef.current = setInterval(() => {
          nextImage(dragDistanceX.current > 0 ? -1 : 1);
          setDraggingSpeed((prevSpeed) => {
            const newSpeed = prevSpeed * 0.95;
            if (newSpeed < 0.05) {
              clearInterval(intervalRef.current);
              return 0;
            }
            return newSpeed;
          });
        }, frameTime);

        setTimeout(() => {
          clearInterval(intervalRef.current);
        }, 3000);
      }
    }
  };

  const toggleScale = () => {
    setScaleLevel((prevScale) => {
      let newScale;
      if (isScalingUp) {
        newScale = prevScale + 1;
        if (newScale >= 4) {
          setIsScalingUp(false);
          newScale = 4;
        }
      } else {
        newScale = prevScale - 1;
        if (newScale <= 1) {
          setIsScalingUp(true);
          newScale = 1;
          setPosition({ x: 0, y: 0 }); // Reset position when fully zoomed out
        }
      }
      return newScale;
    });
  };

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div
      onMouseDown={startDrag}
      onMouseMove={onDrag}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      onTouchStart={startDrag}
      onTouchMove={onDrag}
      onTouchEnd={stopDrag}
      onDoubleClick={toggleScale} // Double Click to toggle scaling
      style={{
        cursor:
          scaleLevel === 1
            ? isDragging
              ? "grabbing"
              : "grab"
            : isScalingUp
            ? "zoom-in"
            : "zoom-out",
        transform: `scale(${1 + (scaleLevel - 1) * 0.25})`, // Scale based on scale level
        transition: "transform 0.3s ease", // Smooth transition for scaling
      }}
      className=" h-full w-full"
    >
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`, // Apply panning translation
          transition: `transform ${draggingSpeed || 0.3}s ease-out`,
        }}
        className="h-full w-full "
      >
        <ImgTag
          src={images[currentIndex]}
          width={320}
          height={480}
          layout="responsive"
          alt={`Slideshow Image ${currentIndex + 1}`}
          className="object-contain h-full mx-auto"
        />
      </div>
    </div>
  );
};

export default Product360View;

const preloadImages = (urls) => {
  urls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
};